import React from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Box, Typography, LinearProgress } from '@mui/material';

function SummaryCard({
    title,
    amount,
    currency,
    icon,
    subtext,
    subtextColor,
    progress,
    progressLabel,
    onClick,
    variant = 'light' // 'light', 'dark', 'warning', or 'error'
}) {
    const { t } = useTranslation();
    const isDark = variant === 'dark';
    const isError = variant === 'error';
    const isWarning = variant === 'warning';
    const isSpecialVariant = isDark || isError || isWarning;

    const bgColor = isError ? 'error.main' : isWarning ? '#D97706' : isDark ? 'primary.main' : 'background.paper';
    const textColor = isSpecialVariant ? (isError ? 'error.contrastText' : '#ffffff') : 'text.primary';
    const borderColor = isError ? 'error.dark' : isWarning ? '#B45309' : isDark ? 'primary.dark' : 'divider';
    const iconColor = isError ? '#FFCDD2' : isWarning ? '#FDE68A' : isDark ? '#A5D0B9' : 'primary.main';
    const subColor = isError ? '#FFCDD2' : isWarning ? '#FEF3C7' : isDark ? '#A5D0B9' : subtextColor;

    return (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                width: '100%',
                height: '100%',
                p: { xs: 2.5, sm: 3 },
                bgcolor: bgColor,
                color: textColor,
                border: '1px solid',
                borderColor: borderColor,
                borderRadius: 1,
                position: 'relative',
                overflow: 'hidden',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': onClick ? {
                    transform: 'translateY(-4px)',
                    boxShadow: isError ? '0 12px 32px rgba(211, 47, 47, 0.25)' : isWarning ? '0 12px 32px rgba(217, 119, 6, 0.25)' : isDark ? '0 12px 32px rgba(1, 45, 29, 0.2)' : '0 12px 32px rgba(28, 28, 24, 0.08)',
                    borderColor: borderColor,
                    bgcolor: isError ? 'error.dark' : isWarning ? '#B45309' : isDark ? 'primary.dark' : 'background.paper',
                } : {}
            }}
        >
            <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 1.5, pr: 4 }}>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            opacity: isSpecialVariant ? 0.8 : 1,
                            color: isSpecialVariant ? 'inherit' : 'text.secondary'
                        }}
                    >
                        {typeof title === 'string' ? t(title) : title}
                    </Typography>
                </Box>

                {React.cloneElement(icon, {
                    sx: {
                        position: 'absolute',
                        top: -2,
                        right: 0,
                        fontSize: '1.75rem',
                        opacity: isSpecialVariant ? 0.4 : 0.25,
                        color: iconColor
                    }
                })}

                <Typography variant="h2" sx={{
                    fontWeight: 800,
                    fontFamily: '"Manrope", sans-serif',
                    lineHeight: 1,
                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                    color: 'inherit',
                    mb: 1,
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap'
                }}>
                    {amount}
                    {currency && (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '0.9rem',
                                opacity: 0.7,
                                fontWeight: 600,
                                ml: 0.75,
                                color: 'inherit'
                            }}
                        >
                            {currency}
                        </Typography>
                    )}
                </Typography>

                <Box sx={{
                    mt: 'auto',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    color: subColor,
                    opacity: isSpecialVariant ? 0.9 : 1
                }}>
                    {progress !== undefined && progress !== null ? (
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.72rem',
                                        color: subColor
                                    }}
                                >
                                    {progressLabel || `${progress.toFixed(1)}%`}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.68rem',
                                        color: subColor,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        opacity: 0.9
                                    }}
                                >
                                    {progress > 100 ? t('OVER BUDGET') : (progress >= 80 ? t('NEAR LIMIT') : t('WITHIN BUDGET'))}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(Math.max(progress, 0), 100)}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: isSpecialVariant ? 'rgba(255, 255, 255, 0.2)' : 'divider',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: isError ? '#FFCDD2' : isWarning ? '#FDE68A' : isDark ? '#A5D0B9' : 'primary.main',
                                        borderRadius: 3,
                                    }
                                }}
                            />
                        </Box>
                    ) : typeof subtext === 'string' ? (
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                            {t(subtext)}
                        </Typography>
                    ) : (
                        <Typography variant="caption" component="div" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'inherit' }}>
                            {subtext}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Paper>
    );
}

export default React.memo(SummaryCard);
